import { useEffect, useState } from 'react';
import { stnkApi } from '@/services/api/stnk';

/**
 * STNK badge count (expired + due within 45 days) for the sidebar.
 *
 * The phone drawer unmounts on close (a deliberate accessibility decision: no
 * hidden dialog left in the DOM), so `Sidebar` remounts on every menu tap. A
 * module-level cache with a short TTL — plus a shared in-flight promise for
 * concurrent mounts — keeps that from firing a request per tap on exactly the
 * slow connections this is for. The cache lives for the page session only, so
 * a real reload always refetches.
 */
const TTL_MS = 60_000;

let cached: { count: number; at: number } | null = null;
let inFlight: Promise<number> | null = null;

const fetchBadge = (): Promise<number> => {
  if (cached && Date.now() - cached.at < TTL_MS) {
    return Promise.resolve(cached.count);
  }
  if (!inFlight) {
    inFlight = stnkApi
      .getStats()
      .then((stats) => {
        const count = stats.expired + stats.within45d;
        cached = { count, at: Date.now() };
        return count;
      })
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
};

/** Test seam: drop the cached count so the next mount refetches. */
export const resetStnkBadgeCache = (): void => {
  cached = null;
  inFlight = null;
};

export const useStnkBadge = (): number => {
  const [badge, setBadge] = useState<number>(() => cached?.count ?? 0);

  useEffect(() => {
    let active = true;
    fetchBadge()
      .then((count) => {
        if (active) setBadge(count);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  return badge;
};
